import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useProjectStore } from '../../store/projectStore';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiX, FiSave, FiArrowLeft, FiClock, FiVideo, FiFileText } = FiIcons;

const CreateProject = () => {
  const navigate = useNavigate();
  const { addProject } = useProjectStore();
  const [formData, setFormData] = useState({
    title: '',
    company: '',
    description: '',
    requirements: '',
    welcomeMessage: '¡Bienvenido a nuestra entrevista! Estamos emocionados de conocerte.',
    farewellMessage: 'Gracias por tu tiempo. Nos pondremos en contacto contigo pronto.',
    thinkingTime: 30,
    responseTime: 60,
    allowRetake: false,
    responseType: 'video',
    requireCV: true,
    questions: []
  });

  const [newQuestion, setNewQuestion] = useState({
    text: '',
    type: 'video',
    thinkingTime: formData.thinkingTime,
    responseTime: formData.responseTime
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleQuestionChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewQuestion({
      ...newQuestion,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const addQuestion = () => {
    if (!newQuestion.text.trim()) {
      toast.error('El texto de la pregunta no puede estar vacío');
      return;
    }

    setFormData({
      ...formData,
      questions: [...formData.questions, { ...newQuestion, id: Date.now() }]
    });

    setNewQuestion({
      text: '',
      type: 'video',
      thinkingTime: formData.thinkingTime,
      responseTime: formData.responseTime
    });
  };

  const removeQuestion = (id) => {
    setFormData({
      ...formData,
      questions: formData.questions.filter(q => q.id !== id)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.questions.length === 0) {
      toast.error('Debes agregar al menos una pregunta');
      return;
    }

    try {
      const newProject = await addProject(formData);
      console.log('Project created successfully:', newProject);
      toast.success('¡Proyecto creado exitosamente!');
      navigate(`/dashboard/projects/${newProject.id}`);
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Error al crear el proyecto: ' + error.message);
    }
  };

  return (
    <div className="p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <button
          onClick={() => navigate('/dashboard/projects')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
          <span>Volver a Proyectos</span>
        </button>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Crear Nuevo Proyecto</h1>
        <p className="text-gray-600">Configura una nueva entrevista para tu proceso de selección</p>
      </motion.div>

      <form onSubmit={handleSubmit}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-lg p-6 mb-8"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-6">Información Básica</h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Título del Puesto *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                required
                placeholder="Ej: Desarrollador Frontend Senior"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Empresa *
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                required
                placeholder="Ej: TechCorp"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descripción del Puesto
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Describe el puesto y las responsabilidades"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Requisitos
            </label>
            <textarea
              name="requirements"
              value={formData.requirements}
              onChange={handleChange}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Ej: 3+ años de experiencia, React, TypeScript, CSS avanzado (separados por comas)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mensaje de Bienvenida
              </label>
              <textarea
                name="welcomeMessage"
                value={formData.welcomeMessage}
                onChange={handleChange}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Mensaje que verán los candidatos al iniciar la entrevista"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mensaje de Despedida
              </label>
              <textarea
                name="farewellMessage"
                value={formData.farewellMessage}
                onChange={handleChange}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Mensaje que verán los candidatos al finalizar la entrevista"
              />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-6 mb-8"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-6">Configuración de la Entrevista</h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <SafeIcon icon={FiClock} className="inline-block w-4 h-4 mr-1" />
                Tiempo para Pensar (segundos)
              </label>
              <input
                type="number"
                name="thinkingTime"
                value={formData.thinkingTime}
                onChange={handleChange}
                min={5}
                max={120}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Tiempo que tendrá el candidato para preparar su respuesta
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <SafeIcon icon={FiClock} className="inline-block w-4 h-4 mr-1" />
                Tiempo de Respuesta (segundos)
              </label>
              <input
                type="number"
                name="responseTime"
                value={formData.responseTime}
                onChange={handleChange}
                min={10}
                max={300}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Tiempo máximo para cada respuesta
              </p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-6 mb-6">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <SafeIcon icon={FiVideo} className="inline-block w-4 h-4 mr-1" />
                Tipo de Respuesta
              </label>
              <select
                name="responseType"
                value={formData.responseType}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="video">Video</option>
                <option value="text">Texto</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <SafeIcon icon={FiFileText} className="inline-block w-4 h-4 mr-1" />
                Requerir CV
              </label>
              <div className="flex items-center h-10">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="requireCV"
                    checked={formData.requireCV}
                    onChange={handleChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  <span className="ml-3 text-sm font-medium text-gray-900">
                    {formData.requireCV ? 'Sí' : 'No'}
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="allowRetake"
                checked={formData.allowRetake}
                onChange={handleChange}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              <span className="ml-3 text-sm font-medium text-gray-900">
                Permitir repetir respuestas
              </span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-14">
              Si está activado, los candidatos podrán volver a grabar sus respuestas
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-lg p-6 mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Preguntas</h2>
            <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
              {formData.questions.length} preguntas
            </span>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nueva Pregunta
            </label>
            <div className="flex items-start gap-2">
              <textarea
                name="text"
                value={newQuestion.text}
                onChange={handleQuestionChange}
                rows={2}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Ej: ¿Cuál ha sido tu experiencia más relevante en desarrollo?"
              />
              <button
                type="button"
                onClick={addQuestion}
                className="px-4 py-2 h-10 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                <span>Agregar</span>
              </button>
            </div>
          </div>

          {formData.questions.length > 0 ? (
            <div className="space-y-4">
              {formData.questions.map((question, index) => (
                <div
                  key={question.id}
                  className="bg-gray-50 p-4 rounded-lg flex items-start justify-between"
                >
                  <div>
                    <div className="flex items-center mb-1">
                      <span className="bg-primary text-white text-xs font-medium px-2 py-0.5 rounded-full mr-2">
                        {index + 1}
                      </span>
                      <span className="text-gray-700 font-medium">{question.text}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Tipo: {question.type === 'video' ? 'Video' : 'Texto'} •
                      Tiempo: {question.responseTime} segundos
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeQuestion(question.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-600">No has agregado preguntas aún</p>
              <p className="text-gray-500 text-sm">Agrega al menos una pregunta para crear el proyecto</p>
            </div>
          )}
        </motion.div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => navigate('/dashboard/projects')}
            className="px-6 py-3 mr-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <SafeIcon icon={FiSave} className="w-5 h-5" />
            <span>Guardar Proyecto</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateProject;